<script setup lang="ts">
import { useToast as usePrimeToast } from 'primevue/usetoast'
import { onMounted, ref, useDialog, navigateTo } from '#imports'
import type { page } from '~/layers/cms/app/types/cmsTypes'
import CreateSiteDialog from './createPageDialog.vue'
import RemoveSiteDialog from './removePageDialog.vue'
import PageSettingsDialog from './pageSettingsDialog.vue'
import { FilterMatchMode } from '@primevue/core/api'
import { usePath } from '~/layers/cms/app/composables/usePath'
import { getPagesList } from '~/layers/cms/app/composables/cms'

const toast = usePrimeToast()
const dialog = useDialog()

const pages = ref<page[]>([])
const filters = ref({
  group: { value: null, matchMode: FilterMatchMode.CONTAINS }, // bedą w przyszłości
  slug: { value: null, matchMode: FilterMatchMode.CONTAINS },
  isCustomPage: { value: null, matchMode: FilterMatchMode.EQUALS }
})

const currentPath = usePath()

const refreshPagesList = async () => {
  const response = await getPagesList()

  if (!response.status_ok) {
    toast.add({
      severity: 'error',
      summary: 'Error',
      detail: 'Nie udało się pobrac listy stron',
      life: 3000
    })
    return
  }

  pages.value = response.data as page[]
}

onMounted(refreshPagesList)

const showAddPageDialog = () => {
  dialog.open(CreateSiteDialog, {
    props: {
      header: 'Dodanie strony',
      modal: true,
      style: {
        width: '80vw',
        height: '80vh'
      }
    },
    onClose: refreshPagesList
  })
}

const openEditPageSettings = (slug: string) => {
  dialog.open(PageSettingsDialog, {
    props: {
      header: 'Ustawienia strony',
      modal: true,
      style: {
        width: '80vw',
        height: '80vh'
      },
      contentStyle: { maxHeight: '75vh', overflowY: 'auto' },
      pt: {
        content: { 'data-lenis-prevent': '' }
      }
    },
    data: { slug },
    onClose: refreshPagesList
  })
}

const showRemovePageDialog = (slug: string) => {
  dialog.open(RemoveSiteDialog, {
    props: { header: 'Usuwanie strony', modal: true },
    data: { slug },
    onClose: refreshPagesList
  })
}
</script>

<template>
  <div
    v-if="pages?.length"
    class="flex flex-col gap-4"
  >
    <DataTable
      v-model:filters="filters"
      :value="pages"
      scrollable
    >
      <template #header>
        <div class="">
          <div class="flex">
            <IconField class="flex">
              <InputIcon>
                <i class="pi pi-search" />
              </InputIcon>
              <InputText
                v-model="filters['slug'].value"
                placeholder="Wyszukaj po slug"
              />
            </IconField>
          </div>
          <Button
            type="button"
            label="Dodaj stronę"
            severity="primary"
            class="mt-4"
            @click="showAddPageDialog"
          />
        </div>
      </template>
      <Column
        field="slug"
        header="Strona"
        sortable
      >
        <template #body="{ data }">
          <div class="flex items-center gap-3">
            <NuxtLink
              :to="data.slug"
              class="font-medium hover:underline"
            >
              {{ data.title }} ({{ data.slug }})
            </NuxtLink>
            <Chip
              v-if="currentPath === data.slug"
              label="Obecna"
              icon="pi pi-check"
              class="text-xs"
            />
          </div>
        </template>
      </Column>
      <Column
        field="isCustomPage"
        header="Typ"
        sortable
      >
        <template #body="{ data }">
          <span
            class="inline-flex text-xs font-medium"
            :class="data.isCustomPage
              ? ' text-blue-700'
              : 'text-orange-600'"
          >
            {{ data.isCustomPage ? 'Własna' : 'Wbudowana' }}
          </span>
        </template>
      </Column>
      <Column
        field="group"
        header="Grupa"
        sortable
      />
      <Column class="w-24">
        <template #body="{ data }">
          <div class="flex flex-row gap-4 justify-end">
            <Button
              v-if="data.isCustomPage"
              type="button"
              label="Usuń"
              severity="warn"
              @click="showRemovePageDialog(data.slug)"
            />
            <Button
              type="button"
              label="Zobacz"
              severity="secondary"
              @click="navigateTo(data.slug)"
            />
            <Button
              type="button"
              label="Zmień"
              severity="secondary"
              @click="openEditPageSettings(data.slug)"
            />
          </div>
        </template>
      </Column>
    </DataTable>
  </div>
  <div v-else>
    Nie załadowano stron
  </div>
</template>
